import React, { useState, useEffect } from "react";
import { Flex, Button, Box, Center, Text} from "@mantine/core";
import { modals } from "@mantine/modals";
import  SRD_DataTable  from "components/DataTable";
import { getGroupControlInfo, updateBatchControl, getControllerIdList } from "pages/Admin/Device/apis/adminDeviceApis";
import { useQuery } from "react-query";
import { useRecoilState } from "recoil";
import { userState } from "pages/Login/atom/userState";
import { useLocation } from 'react-router-dom';
import { useSrdkoreaSocket } from "../../../ws/useSrdkoreaSocket";

const parseHexToDecimal = (value) => {
  return parseInt(value, 16);
};

const parseAlarm = (alarm, messageKey, expectedLength) => {
  const raw = alarm?.[messageKey];

  if (!raw) return null;

  // 상태값 128 bytes = 256 hex characters
  // 제어값 64 bytes = 128 hex characters
  if (raw.length !== expectedLength) {
    console.warn(
      `[parseAlarm] 잘못된 전문 길이: ${raw.length}자`
    );
    return null;
  }

  // HEX 문자열인지 확인
  if (!/^[0-9a-fA-F]+$/.test(raw)) {
    console.warn("[parseAlarm] HEX 형식이 아닙니다.");
    return null;
  }

  return {
    csn: raw.substring(0, 20),
    len: parseHexToDecimal(raw.substring(20, 24)),
    raw,
    data: (raw.substring(24).match(/.{4}/g) || [])
      .map((value, index) => ({
        index: index + 7,
        hex: value,
        decimal: parseHexToDecimal(value),
      })),
  };
};

const formatAlarmTime = (date = new Date()) => {
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  return `${hh}:${mi}:${ss}`;
};

const processStatusItem = (item, commandMap = {}) => {
  const chNo = (item.ch_no || "").split(",");
  const chVal = (item.ch_values || "").split(",");
  const chPrcsdg = item.ch_prcsdg
    ? item.ch_prcsdg.split(",")
    : [];

  const result = { ...item };

  // 채널값 세팅
  for (let i = 0; i < chNo.length; i++) {
    result[`ch${chNo[i]}`] = chVal[i];

    if (chPrcsdg[i]) {
      result[`ch${chNo[i]}_prcsdg`] = chPrcsdg[i];
    }
  }

  // 분사 상태
  let jet_sts = "분사정지";
  if (
    result.ch38 === "1" ||
    result.ch39 === "1" ||
    result.ch7 === "1"
  ) {
    jet_sts = "분사중";
  }

  // 명령 상태
  let cmd_sts = "----";
  const command = commandMap[result.bsd_id];
  if (command) {
    if (command["8"] === "1") {
      cmd_sts = "중지 명령";
    } else if (command["7"] === "1") {
      cmd_sts = "분사 명령";
    }
  }

  // 노면온도
  let road_temp = "-";
  if (result.ch51 && result.ch51_prcsdg === "1") {
    if (Number(result.ch51) > 32768) {
      road_temp =
        (Number(result.ch51) - 65536).toString() + "℃";
    } else {
      road_temp = result.ch51 + "℃";
    }
  }

  // 대기습도
  let humidity = "-";
  if (result.ch53 && result.ch53_prcsdg === "1") {
    humidity = result.ch53 + "%";
  }

  // 액체감지
  let liquid_detect = "-";
  if (result.ch44 && result.ch44_prcsdg === "1") {
    liquid_detect =
      result.ch44 === "1" ? "만족" : "불만족";
  }

  // 수위
  const water_lv = result.ch14
    ? result.ch14 + "%"
    : "-";

  // 제어방식
  let smartBtn;
  if (chNo.length < 9) {
    smartBtn = "직접제어";
  } else {
    smartBtn =
      result.ch60 === "0"
        ? "직접제어"
        : "AI형제어";
  }

  // 분사 가능 여부
  const isPossibleToJet =
    result.vido_url !== "COMM_ERR" &&
    smartBtn !== "AI형제어";

  return {
    ...result,
    cmd_sts,
    jet_sts,
    road_temp,
    humidity,
    liquid_detect,
    water_lv,
    smartBtn,
    isPossibleToJet,
  };
};

const processRealtimeStatusItem = (item) => {
  const result = { ...item };

  // --------------------------------
  // 분사 상태
  // CH7, CH38, CH39 중 하나라도 1이면 분사중
  // --------------------------------
  result.jet_sts =
    result.ch38 === "1" ||
    result.ch39 === "1" ||
    result.ch7 === "1"
      ? "분사중"
      : "분사정지";

  // --------------------------------
  // 명령 상태
  // --------------------------------
  if (result.ctrl_ch8 === "1") {
    result.cmd_sts = "중지 명령";
  } else if (result.ctrl_ch7 === "1") {
    result.cmd_sts = "분사 명령";
  } else {
    result.cmd_sts = "----";
  }

  // --------------------------------
  // 노면온도 CH51
  // --------------------------------
  if (result.ch51 && result.ch51_prcsdg === "1") {
    const temperature = Number(result.ch51);

    result.road_temp =
      temperature > 32768
        ? `${temperature - 65536}℃`
        : `${temperature}℃`;
  } else {
    result.road_temp = "-";
  }

  // --------------------------------
  // 대기습도 CH53
  // --------------------------------
  if (result.ch53 && result.ch53_prcsdg === "1") {
    result.humidity = `${result.ch53}%`;
  } else {
    result.humidity = "-";
  }

  // --------------------------------
  // 액체감지 CH44
  // --------------------------------
  if (result.ch44 && result.ch44_prcsdg === "1") {
    result.liquid_detect =
      result.ch44 === "1"
        ? "만족"
        : "불만족";
  } else {
    result.liquid_detect = "-";
  }

  // --------------------------------
  // 수위 CH14
  // --------------------------------
  result.water_lv =
    result.ch14 !== undefined && result.ch14 !== null
      ? `${result.ch14}%`
      : "-";

  // --------------------------------
  // 분사 가능 여부
  // --------------------------------
  result.isPossibleToJet =
    result.vido_url !== "COMM_ERR" &&
    result.smartBtn !== "AI형제어";

  return result;
};

function BatchControlForm() {
  const [user] = useRecoilState(userState);
  const group = user?.fn_group_id;
  const [exeCnt, setExeCnt] = useState({start:0, stop:0 , totalDev:0, possibleDev:0});
  const [ctrlChList, setCtrlChList] = useState([]);
  const [stsChList, setStsChList] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { data, dataUpdatedAt } = useQuery(["batchData", group], () => getGroupControlInfo(group), {refetchOnWindowFocus: false});
  const [commList, setCommList] = useState([]);
  const [bsdIds, setBsdIds] = useState([]);
  const [lastAlarm, setLastAlarm] = useState(null);
  const location = useLocation();
  const isDebug = new URLSearchParams(location.search).get("debug") === "y";

  useQuery(["commData", group], () => getControllerIdList(group), {
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    onSuccess: (commData) => {
      setCommList(commData);

      setStsChList((prev) =>
        prev.map((item) => {
          const comm = commData.find(
            (commItem) =>
              String(commItem.bsd_id) === String(item.bsd_id)
          );

          if (!comm) {
            return item;
          }

          return {
            ...item,
            vido_url: comm.vido_url,
          };
        })
      );
    },
  });

  useSrdkoreaSocket(
    user ? `srdkorea_${user.user_id}` : null,
    bsdIds,
    null,
    (alarm) => {
      const alarmTime = formatAlarmTime();

      setLastAlarm({
        time: alarmTime,
        data: alarm,
      });

      const parsedSttus = parseAlarm(alarm, "message_sttus", 256);
      const parsedCtrl = parseAlarm(alarm, "message_ctrl", 128);

      if (!parsedSttus && !parsedCtrl) return;

      const bsdId = alarm.bsdId.replace("bsd_", "");

      setStsChList((prev) =>
        prev.map((item) => {
          if (String(item.bsd_id) !== String(bsdId)) {
            return item;
          }

          const updatedItem = {
            ...item,
            vido_url: "COMM_OK",
            lastUpdateTime: alarmTime,
          };

          // 상태값
          if (parsedSttus) {
            parsedSttus.data.forEach(({ index, decimal }) => {
              updatedItem[`ch${index}`] = String(decimal);
            });
          }

          // 제어값
          if (parsedCtrl) {
            parsedCtrl.data.forEach(({ index, decimal }) => {
              updatedItem[`ctrl_ch${index}`] = String(decimal);
            });
          }

          return processRealtimeStatusItem(updatedItem);
        })
      );
    }
  );

  useEffect(() => {
    const exeBatch = stsChList.filter(
      (item) => item.jet_sts === "분사중"
    ).length;

    const stopBatch = stsChList.filter(
      (item) => item.jet_sts === "분사정지"
    ).length;

    const totalDev = stsChList.length;

    const possibleDev = stsChList.filter(
      (item) =>
        item.isPossibleToJet &&
        item.jet_sts === "분사정지"
    ).length;

    setExeCnt({
      start: exeBatch,
      stop: stopBatch,
      totalDev,
      possibleDev,
    });
  }, [stsChList]);

  useEffect(() => {
    if (!data || !data.status?.length) return;

    const bsdIdList = [
      ...new Set(
        data.status
          .filter((item) => item.bsd_id)
          .map((item) => `bsd_${item.bsd_id}`)
      ),
    ];

    setBsdIds(bsdIdList);
    setCtrlChList(data.control || []);

    const commandMap = {};

    (data.control || []).forEach((ctrl) => {
      if (!commandMap[ctrl.bsd_id]) {
        commandMap[ctrl.bsd_id] = {};
      }

      commandMap[ctrl.bsd_id][ctrl.chnnl_no] =
        ctrl.chnnl_ctrl_value;
    });

    const processedStatus = data.status.map((item) =>
      processStatusItem(
        {
          ...item,
          lastUpdateTime: formatAlarmTime(),
        },
        commandMap
      )
    );

    setStsChList(processedStatus);
  }, [data, dataUpdatedAt]);

  const startBatch = () => {
      modals.openConfirmModal({
        title: "염수 일괄분사 명령",
        centered: true,
        zIndex: 2050,
        children: (
          <Center>
            <Text>{selectedRecords.length}개 장치에서의 염수 일괄분사를 실행 하시겠습니까?</Text>
          </Center>
        ),
        labels: { confirm: "염수분사 실행", cancel: "취소" },
        confirmProps: { color: "red" },
        onConfirm: () => {
          const selectedBsdList = selectedRecords.map((record) => record.bsd_id);
          const selectedCtrlList = ctrlChList.filter(
            (item) => selectedBsdList.includes(item.bsd_id) && item.chnnl_no === '7'
          ).map((v) => ({...v,chnnl_ctrl_value:'1',rm:"일괄분사"}));
          updateBatchControl(selectedCtrlList);
          setSelectedRecords([]);
        },
    });
  };

  const stopBatch = () => {
      modals.openConfirmModal({
        title: "염수 일괄분사 중지 ",
        centered: true,
        zIndex: 2050,
        children: (
          <Center>
            <Text>{selectedRecords.length}개 장치에서의 염수분사를 중지 하시겠습니까?</Text>
          </Center>
        ),
        labels: { confirm: "염수분사 중지", cancel: "취소" },
        confirmProps: { color: "red" },
        onConfirm: () => {
          const selectedBsdList = selectedRecords.map((record) => record.bsd_id);
          const selectedCtrlList = ctrlChList.filter(
            (item) => selectedBsdList.includes(item.bsd_id) && item.chnnl_no === '8'
          ).map((v) => ({...v,chnnl_ctrl_value:'1',rm:"일괄분사"}));
          updateBatchControl(selectedCtrlList);

          setSelectedRecords([]);
        },
      });
    };

  const columns = [
    {
      accessor: "index",
      title: "번호",
      textAlignment: "center",
      width: 30,
      render: (record, index) => index + 1,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "bsd_nm",
      title: "장치명",
      textAlignment: "center",
      width: 120,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "jet_sts",
      title: "분사",
      textAlignment: "center",
      width: 58,
      render: ({ jet_sts }) => (
          <Box c={jet_sts === "분사정지" ? "black" : "#E03131"}>
            {jet_sts}
          </Box>
      ),
      cellsStyle: (e) =>
        e.jet_sts === "분사중"
            ? {
                fontWeight: 'bold',
                color: '#E03131',
                background: '#FFC9C9',
              }
              : undefined,
    },
    {
      accessor: "cmd_sts",
      title: "명령",
      textAlignment: "center",
      width: 58,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "vido_url",
      title: "통신상태",
      textAlignment: "center",
      width: 58,
      render: (e) => (
        <Box c={(e.jet_sts !== "분사중") && (e.vido_url === "COMM_ERR" ? "#E03131" : "black")}>
          {e.vido_url === "COMM_ERR" ? "통신오류" : "통신정상"}
        </Box>
      ),
      cellsStyle: (e) =>
          (e.jet_sts === "분사중")
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "smartBtn",
      title: "제어방식",
      textAlignment: "center",
      width: 58,
      render: (e) => (
        <Box c={(e.jet_sts !== "분사중") && (e.smartBtn === "AI형제어" ? "#E03131" : "black")}>
          {e.smartBtn}
        </Box>
      ),
    cellsStyle: (e) =>
          (e.jet_sts === "분사중")
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "road_temp",
      title: "노면온도",
      textAlignment: "center",
      width: 53,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "humidity",
      title: "대기습도",
      textAlignment: "center",
      width: 53,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "liquid_detect",
      title: "액체감지",
      textAlignment: "center",
      width: 53,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "water_lv",
      title: "수위",
      textAlignment: "center",
      width: 35,
      cellsStyle: (e) =>
          e.jet_sts === "분사중"
              ? {
                  fontWeight: 'bold',
                  color: '#E03131',
                  background: '#FFC9C9',
                }
              : undefined,
    },
    {
      accessor: "lastUpdateTime",
      title: "업데이트",
      textAlignment: "center",
      width: 56,
      render: (record) => (
        <Text size="sm">
          {record.lastUpdateTime ?? "-"}
        </Text>
      ),
      cellsStyle: (e) =>
        e.jet_sts === "분사중"
          ? {
              fontWeight: "bold",
              color: "#E03131",
              background: "#FFC9C9",
            }
          : undefined,
    },
  ];


  return (
    <>
    <Flex mt={10} gap={5} direction={"column"} className="object-management">
      <div style={{ width: "900px", height: "600px"}}>
        <SRD_DataTable
            data={stsChList ?? []}
            columns={columns}
            searchBar
            idAccessor="bsd_nm"
            selectedRecords={selectedRecords}
            setSelectedRecords={setSelectedRecords}
            isRecordSelectable={(record) => record.isPossibleToJet === true}
            selectionColumnWidth={160}
        />
      </div>
      <div style={{ width: "900px", height: "80px"}}>
        <Flex mt={10} gap={30} justify={"center"} >
          <Button
            variant="filled"
            color={"red"}
            onClick={startBatch}
            disabled={selectedRecords.length === 0 || (exeCnt.possibleDev < 1)}
          >
            염수 일괄분사 실행
          </Button>
          <Button
            variant="filled"
            onClick={stopBatch}
            disabled={selectedRecords.length === 0 || (exeCnt.start < 1)}
          >
            염수 일괄분사 중지
          </Button>
        </Flex>
      </div>

      {isDebug && lastAlarm && (() => {
        const parsedSttus = parseAlarm(lastAlarm.data, "message_sttus", 256);
        const parsedCtrl = parseAlarm(lastAlarm.data, "message_ctrl", 128);

        return (
          <div>
            <h3>Latest Alarm</h3>

            <div>
              <b>{lastAlarm.data.bsdId}: </b>
              <span>{lastAlarm.time}</span>
            </div>

            {/* Status */}
            {parsedSttus && (
              <div>
                <h4>Status</h4>

                <div>
                  <span>
                    CSN: {parsedSttus.csn} / Payload Data Length:{" "}
                    {parsedSttus.len}
                  </span>
                </div>

                <div>
                  <span>
                    {lastAlarm.data.message_sttus}
                  </span>
                </div>

                <div>
                  {parsedSttus.data.map((item) => (
                    <div key={item.index}>
                      CH {item.index} : {item.hex} → {item.decimal}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Control */}
            {parsedCtrl && (
              <div>
                <h4>Control</h4>

                <div>
                  <span>
                    CSN: {parsedCtrl.csn} / Payload Data Length:{" "}
                    {parsedCtrl.len}
                  </span>
                </div>

                <div>
                  <span>
                    {lastAlarm.data.message_ctrl}
                  </span>
                </div>

                <div>
                  {parsedCtrl.data.map((item) => (
                    <div key={item.index}>
                      CH {item.index} : {item.hex} → {item.decimal}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </Flex>
    </>
  );
}

export default BatchControlForm;
